/**
 * `useServiceLibrary` — live read of every service library item
 * in the org, with optional client-side filtering by category +
 * text search.
 *
 * Mirrors `useMaterialLibrary` (same shape, same sort, same
 * filter semantics) so callers can swap between the two with
 * minimal cognitive overhead. Powers the Services Library page
 * + the quote form's "From Services Library" modal.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';
import type {
  ServiceCategory,
  ServiceLibraryItem,
} from '@/lib/serviceLibrary';

export type UseServiceLibraryResult = {
  data: ServiceLibraryItem[];
  loading: boolean;
};

export function useServiceLibrary(
  orgId: string | null | undefined,
  /** Optional case-insensitive substring filter — matched against
   *  name, description, hsn, unit, category label. */
  searchText?: string,
  /** Optional category filter. Falsy = all categories. */
  category?: ServiceCategory | null,
): UseServiceLibraryResult {
  const [all, setAll] = useState<ServiceLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setAll([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(collection(db, 'serviceLibrary'), where('orgId', '==', orgId)),
      (snap) => {
        const rows: ServiceLibraryItem[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<ServiceLibraryItem, 'id'>),
        }));
        rows.sort((a, b) => a.name.localeCompare(b.name));
        setAll(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useServiceLibrary] snapshot error:', err);
        setAll([]);
        setLoading(false);
      },
    );
  }, [orgId]);

  // Client-side filter — cheap because a studio rarely has more
  // than a few dozen saved services. Filtering server-side would
  // need per-category snapshots which fragment the cache for
  // no real benefit at this size.
  const filtered = useMemo(() => {
    const q = (searchText ?? '').trim().toLowerCase();
    return all.filter((it) => {
      if (category && it.category !== category) return false;
      if (q) {
        const blob = [
          it.name,
          it.description ?? '',
          it.hsn ?? '',
          it.unit,
          it.category,
        ]
          .join(' ')
          .toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [all, searchText, category]);

  return { data: filtered, loading };
}
